import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { TalkPlanMaster, TalkPlanMasterUpdateRequest } from '../../types/talkPlanMaster';
import { updateTalkPlanMaster } from '../../api/talkPlanMasters';
import { TimeDisplay } from '../common';
import TalkPlanMasterPublishConfirmDialog from './TalkPlanMasterPublishConfirmDialog';

interface TalkPlanMasterEditDialogProps {
  open: boolean;
  onClose: () => void;
  talkPlanMaster: TalkPlanMaster;
  onSuccess: () => void;
}

interface FieldErrors {
  name?: string;
  price?: string;
  period_days?: string;
  reply_frequency?: string;
  description?: string;
  remarks?: string;
}

const TalkPlanMasterEditDialog: React.FC<TalkPlanMasterEditDialogProps> = ({
  open,
  onClose,
  talkPlanMaster,
  onSuccess,
}) => {
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ errors, setErrors ] = useState<string[]>([]);
  const [ fieldErrors, setFieldErrors ] = useState<FieldErrors>({});
  const [ showPublishConfirm, setShowPublishConfirm ] = useState(false);
  const [ originalPublished, setOriginalPublished ] = useState(false);
  // フォームの状態
  const [ formData, setFormData ] = useState<TalkPlanMasterUpdateRequest>({
    name: '',
    price: '' as any,
    period_days: '' as any,
    reply_frequency: '',
    description: '',
    published: false,
    remarks: '',
  });
  // ダイアログが開いた時の初期化
  useEffect(() => {
    if (open && talkPlanMaster) {
      setFormData({
        name: talkPlanMaster.name || '',
        price: talkPlanMaster.price,
        period_days: talkPlanMaster.period_days,
        reply_frequency: talkPlanMaster.reply_frequency || '',
        description: talkPlanMaster.description || '',
        published: talkPlanMaster.published,
        remarks: talkPlanMaster.remarks || '',
      });
      setOriginalPublished(talkPlanMaster.published); // 元の公開状態を保持
      setError(null);
      setErrors([]);
      setFieldErrors({});
      setShowPublishConfirm(false);
    }
  }, [ open, talkPlanMaster ]);
  // フォームの値を更新
  const handleChange = (field: keyof TalkPlanMasterUpdateRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (field === 'published') {
      const target = event.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [field]: target.checked
      }));
    } else {
      const value = event.target.value;
      setFormData(prev => ({
        ...prev,
        [field]: field === 'price' || field === 'period_days'
          ? value === '' ? '' : Number(value)
          : value
      }));
    }
    // フィールドのエラーをクリア
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors((prev: FieldErrors) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  // 送信処理（バリデーションなし、外部APIが処理）
  const handleSubmit = async() => {
    // 非公開から公開に変更する場合は確認ダイアログを表示
    if (!originalPublished && formData.published && !showPublishConfirm) {
      setShowPublishConfirm(true);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setErrors([]);
    try {
      await updateTalkPlanMaster(talkPlanMaster.id, {
        ...formData,
        remarks: formData.remarks || undefined,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('対話プラン更新エラー:', error);
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        setErrors(serverErrors);
        // サーバーエラーをフィールドにマッピング
        const mappedFieldErrors: FieldErrors = {};
        serverErrors.forEach((errMsg: string) => {
          if (errMsg.includes('プラン名') || errMsg.includes('name')) {
            mappedFieldErrors.name = errMsg;
          } else if (errMsg.includes('金額') || errMsg.includes('price')) {
            mappedFieldErrors.price = errMsg;
          } else if (errMsg.includes('期間') || errMsg.includes('period_days')) {
            mappedFieldErrors.period_days = errMsg;
          } else if (errMsg.includes('返信頻度') || errMsg.includes('reply_frequency')) {
            mappedFieldErrors.reply_frequency = errMsg;
          } else if (errMsg.includes('プラン詳細') || errMsg.includes('内容') || errMsg.includes('description')) {
            mappedFieldErrors.description = errMsg;
          } else if (errMsg.includes('備考') || errMsg.includes('remarks')) {
            mappedFieldErrors.remarks = errMsg;
          }
        });
        setFieldErrors(mappedFieldErrors);
      } else {
        setError('対話プランの更新に失敗しました。再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
      setShowPublishConfirm(false);
    }
  };
  // 確認ダイアログからの続行処理
  const handleConfirmPublish = () => {
    setShowPublishConfirm(false);
    handleSubmit();
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={(_event: React.SyntheticEvent, reason?: string) => {
          // backdropClickやescapeKeyDownによる閉じるを無効化
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onClose();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">対話プラン編集</Typography>
            <Typography variant="caption" color="text.secondary">
              作成日時: <TimeDisplay timestamp={talkPlanMaster.created_at}/>
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(error || errors.length > 0) && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error && <div>{error}</div>}
                  {errors.map((err: string, index: number) => (
                    <div key={index}>{err}</div>
                  ))}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="プラン名"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange('name')}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || "最大30文字"}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="金額"
                type="number"
                fullWidth
                required
                value={formData.price === '' ? '' : formData.price}
                onChange={handleChange('price')}
                error={!!fieldErrors.price}
                helperText={fieldErrors.price || "0円（無料）または50〜999,999円"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">円</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, max: 999999 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="期間"
                type="number"
                fullWidth
                required
                value={formData.period_days === '' ? '' : formData.period_days}
                onChange={handleChange('period_days')}
                error={!!fieldErrors.period_days}
                helperText={fieldErrors.period_days || "1〜999日"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">日</InputAdornment>
                  ),
                }}
                inputProps={{ min: 1, max: 999 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="返信頻度"
                fullWidth
                required
                value={formData.reply_frequency}
                onChange={handleChange('reply_frequency')}
                error={!!fieldErrors.reply_frequency}
                helperText={fieldErrors.reply_frequency || "最大30文字"}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="プラン詳細"
                fullWidth
                required
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange('description')}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description || "最大1,000文字"}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="備考"
                fullWidth
                multiline
                rows={3}
                value={formData.remarks}
                onChange={handleChange('remarks')}
                error={!!fieldErrors.remarks}
                helperText={fieldErrors.remarks || "任意"}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleChange('published')}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    {formData.published ? '公開' : '非公開'}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? '更新中...' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 公開確認ダイアログ */}
      <TalkPlanMasterPublishConfirmDialog
        open={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={handleConfirmPublish}
      />
    </>
  );
};
export default TalkPlanMasterEditDialog;
